<template>
  <div class="h-full flex flex-col">
    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex items-center justify-center text-gray-400">
      <span class="animate-pulse">Loading graph topology...</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex-1 flex items-center justify-center text-red-500 text-sm">
      {{ error }}
    </div>

    <!-- Graph -->
    <div v-else class="flex-1 relative" ref="container">
      <svg ref="svg" class="w-full h-full"></svg>

      <!-- Tooltip -->
      <div
        v-if="tooltip"
        class="absolute pointer-events-none bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-xs z-10 max-w-[360px]"
        :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
      >
        <div class="font-semibold text-gray-900 text-sm">{{ tooltip.title }}</div>
        <div v-if="tooltip.subtitle" class="text-gray-500 mt-0.5">{{ tooltip.subtitle }}</div>
        <div v-if="tooltip.details" class="text-gray-400 mt-1.5 leading-relaxed whitespace-pre-line">{{ tooltip.details }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'

const ENTITY_COLORS = {
  NPI_INDIVIDUAL: '#3b82f6',
  NPI_ORGANIZATION: '#6366f1',
  TIN: '#10b981',
  PAC_GROUP: '#14b8a6',
  ACO: '#f59e0b',
  ACO_YEAR: '#eab308',
  FACILITY: '#ef4444',
  SPECIALTY: '#8b5cf6',
}

const ENTITY_LABELS = {
  NPI_INDIVIDUAL: 'Individual NPI',
  NPI_ORGANIZATION: 'Organization NPI',
  TIN: 'TIN',
  PAC_GROUP: 'PAC Group',
  ACO: 'ACO',
  ACO_YEAR: 'ACO Year',
  FACILITY: 'Facility',
  SPECIALTY: 'Specialty',
}

const ATTR_LABELS = {
  specialty: 'Specialty',
  location: 'Location',
  contact: 'Contact',
  provider_demographics: 'Demographics',
  medicare_utilization: 'Medicare Utilization',
  medicaid_utilization: 'Medicaid Utilization',
  attributed_beneficiaries: 'Attribution',
  ehr_vendor: 'EHR Vendor',
  pecos_specialty: 'PECOS Specialty',
  dac_specialty: 'DAC Specialty',
  aco_profile: 'ACO Profile',
  aco_contacts: 'ACO Contacts',
  aco_performance: 'ACO Performance',
  aco_year_performance: 'Year Performance',
  aco_waivers: 'ACO Waivers',
  aco_program: 'ACO Program',
  facility_profile: 'Facility Profile',
  facility_type: 'Facility Type',
  group_profile: 'Group Profile',
}

const DATA_COLOR = '#d1d5db'

export default {
  name: 'GraphTab',
  data() {
    return {
      loading: false,
      error: null,
      topology: null,
      tooltip: null,
      simulation: null,
    }
  },
  mounted() {
    this.fetchTopology()
  },
  beforeUnmount() {
    if (this.simulation) this.simulation.stop()
  },
  methods: {
    async fetchTopology() {
      this.loading = true
      this.error = null
      try {
        const response = await this.$axios.get('/api/growth/graph-topology/')
        this.topology = response.data
        this.$nextTick(() => {
          this.$nextTick(() => this.renderGraph())
        })
      } catch (err) {
        this.error = err.response?.data?.detail || 'Failed to load graph topology'
      } finally {
        this.loading = false
      }
    },

    renderGraph() {
      if (!this.$refs.container || !this.$refs.svg) return

      const { nodes: rawNodes, edges: rawEdges, interpretations } = this.topology
      const svg = d3.select(this.$refs.svg)
      svg.selectAll('*').remove()

      const container = this.$refs.container
      const width = container.clientWidth || 800
      const height = container.clientHeight || 600

      // --- Build nodes ---
      // Entity type nodes (large)
      const maxCount = d3.max(rawNodes, d => d.count)
      const sizeScale = d3.scaleSqrt().domain([1, maxCount]).range([20, 65])

      const entityNodes = rawNodes.map(n => ({
        id: n.id,
        type: 'entity',
        count: n.count,
        label: ENTITY_LABELS[n.id] || n.id,
        color: ENTITY_COLORS[n.id] || '#9ca3af',
        radius: sizeScale(n.count),
      }))

      // Group interpretations by entity_type
      const interpByEntity = {}
      for (const i of interpretations) {
        if (!interpByEntity[i.entity_type]) interpByEntity[i.entity_type] = []
        interpByEntity[i.entity_type].push(i)
      }

      // Data attribute nodes (small satellites)
      const dataNodes = []
      const dataEdges = []
      for (const [entityType, attrs] of Object.entries(interpByEntity)) {
        for (const attr of attrs) {
          const nodeId = `${entityType}::${attr.attribute}`
          dataNodes.push({
            id: nodeId,
            type: 'data',
            attribute: attr.attribute,
            label: ATTR_LABELS[attr.attribute] || attr.attribute,
            count: attr.count,
            sources: attr.sources || [],
            parentEntity: entityType,
            color: DATA_COLOR,
            radius: 8,
          })
          dataEdges.push({
            source: entityType,
            target: nodeId,
            type: 'has_data',
          })
        }
      }

      const allNodes = [...entityNodes, ...dataNodes]

      // Entity-to-entity edges (aggregated by source+target)
      const edgeMap = {}
      for (const e of rawEdges) {
        const key = `${e.source}→${e.target}`
        if (!edgeMap[key]) {
          edgeMap[key] = { source: e.source, target: e.target, rels: [], totalCount: 0, type: 'relationship' }
        }
        edgeMap[key].rels.push({ rel_type: e.rel_type, count: e.count })
        edgeMap[key].totalCount += e.count
      }
      const relEdges = Object.values(edgeMap)

      const allEdges = [...relEdges, ...dataEdges]

      // Edge width scale (only for relationship edges)
      const maxEdgeCount = d3.max(relEdges, d => d.totalCount) || 1
      const edgeWidthScale = d3.scaleSqrt().domain([1, maxEdgeCount]).range([1.5, 8])

      // --- SVG setup ---
      const defs = svg.append('defs')
      defs.append('marker')
        .attr('id', 'arrow')
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 10).attr('refY', 0)
        .attr('markerWidth', 6).attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path').attr('d', 'M0,-5L10,0L0,5').attr('fill', '#94a3b8')

      const g = svg.append('g')
      svg.call(d3.zoom().scaleExtent([0.2, 5]).on('zoom', (event) => g.attr('transform', event.transform)))

      // --- Relationship links ---
      const relLink = g.append('g').selectAll('line.rel')
        .data(relEdges)
        .join('line')
        .attr('class', 'rel')
        .attr('stroke', '#94a3b8')
        .attr('stroke-width', d => edgeWidthScale(d.totalCount))
        .attr('stroke-opacity', 0.5)
        .attr('marker-end', 'url(#arrow)')

      // Relationship labels
      const relLabel = g.append('g').selectAll('text.rel-label')
        .data(relEdges)
        .join('text')
        .attr('class', 'rel-label')
        .attr('text-anchor', 'middle')
        .attr('dy', -6)
        .attr('font-size', '9px')
        .attr('fill', '#64748b')
        .attr('font-weight', '500')
        .attr('pointer-events', 'none')
        .text(d => d.rels.map(r => r.rel_type).join(', '))

      // --- Data links (thin, dashed) ---
      const dataLink = g.append('g').selectAll('line.data')
        .data(dataEdges)
        .join('line')
        .attr('class', 'data')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-opacity', 0.7)

      // --- Nodes ---
      const node = g.append('g').selectAll('g.node')
        .data(allNodes)
        .join('g')
        .attr('class', 'node')
        .call(d3.drag()
          .on('start', (event, d) => {
            if (!event.active) this.simulation.alphaTarget(0.3).restart()
            d.fx = d.x; d.fy = d.y
          })
          .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y })
          .on('end', (event, d) => {
            if (!event.active) this.simulation.alphaTarget(0)
            d.fx = null; d.fy = null
          })
        )

      // Entity circles
      node.filter(d => d.type === 'entity')
        .append('circle')
        .attr('r', d => d.radius)
        .attr('fill', d => d.color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2.5)
        .attr('opacity', 0.9)

      // Entity labels
      node.filter(d => d.type === 'entity')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', d => d.radius + 16)
        .attr('font-size', '12px')
        .attr('font-weight', '700')
        .attr('fill', '#1f2937')
        .text(d => d.label)

      node.filter(d => d.type === 'entity')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', d => d.radius + 29)
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text(d => d.count.toLocaleString() + ' entities')

      // Data attribute circles (small)
      node.filter(d => d.type === 'data')
        .append('circle')
        .attr('r', d => d.radius)
        .attr('fill', '#f3f4f6')
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1)

      // Data attribute labels
      node.filter(d => d.type === 'data')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', d => d.radius + 12)
        .attr('font-size', '9px')
        .attr('fill', '#6b7280')
        .text(d => d.label)

      // --- Tooltips ---
      const self = this
      node.on('mouseover', function (event, d) {
        const rect = container.getBoundingClientRect()
        if (d.type === 'entity') {
          const attrs = interpByEntity[d.id] || []
          const details = attrs.length > 0
            ? attrs.map(a => `${ATTR_LABELS[a.attribute] || a.attribute}: ${a.count.toLocaleString()} rows`
              + (a.sources?.length ? ` (${a.sources.join(', ')})` : '')
            ).join('\n')
            : 'No data attributes'
          self.tooltip = {
            x: event.clientX - rect.left + 14,
            y: event.clientY - rect.top - 10,
            title: d.label,
            subtitle: `${d.count.toLocaleString()} entities`,
            details,
          }
        } else {
          self.tooltip = {
            x: event.clientX - rect.left + 14,
            y: event.clientY - rect.top - 10,
            title: d.label,
            subtitle: `${d.count.toLocaleString()} rows on ${ENTITY_LABELS[d.parentEntity] || d.parentEntity}`,
            details: d.sources?.length ? `Sources: ${d.sources.join(', ')}` : null,
          }
        }
      }).on('mouseout', () => { self.tooltip = null })

      relLink.on('mouseover', function (event, d) {
        const rect = container.getBoundingClientRect()
        self.tooltip = {
          x: event.clientX - rect.left + 14,
          y: event.clientY - rect.top - 10,
          title: `${ENTITY_LABELS[d.source.id || d.source] || d.source} → ${ENTITY_LABELS[d.target.id || d.target] || d.target}`,
          subtitle: `${d.totalCount.toLocaleString()} relationships`,
          details: d.rels.map(r => `${r.rel_type}: ${r.count.toLocaleString()}`).join('\n'),
        }
      }).on('mouseout', () => { self.tooltip = null })

      // --- Force simulation ---
      this.simulation = d3.forceSimulation(allNodes)
        .force('link', d3.forceLink(allEdges).id(d => d.id).distance(d => {
          if (d.type === 'has_data') return 60
          return 200
        }))
        .force('charge', d3.forceManyBody().strength(d => d.type === 'data' ? -30 : -800))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('collision', d3.forceCollide().radius(d => d.radius + (d.type === 'data' ? 8 : 25)))
        .on('tick', () => {
          relLink
            .attr('x1', d => d.source.x)
            .attr('y1', d => d.source.y)
            .attr('x2', d => {
              const dx = d.target.x - d.source.x, dy = d.target.y - d.source.y
              const dist = Math.sqrt(dx * dx + dy * dy) || 1
              return d.target.x - (dx / dist) * (d.target.radius + 6)
            })
            .attr('y2', d => {
              const dx = d.target.x - d.source.x, dy = d.target.y - d.source.y
              const dist = Math.sqrt(dx * dx + dy * dy) || 1
              return d.target.y - (dy / dist) * (d.target.radius + 6)
            })

          relLabel
            .attr('x', d => (d.source.x + d.target.x) / 2)
            .attr('y', d => (d.source.y + d.target.y) / 2)

          dataLink
            .attr('x1', d => d.source.x)
            .attr('y1', d => d.source.y)
            .attr('x2', d => d.target.x)
            .attr('y2', d => d.target.y)

          node.attr('transform', d => `translate(${d.x},${d.y})`)
        })
    },
  },
}
</script>
